/* eslint-disable no-alert */
import AjaxDataService from 'services/ajax.service';
import StringHelperService from 'services/string-helper.service';
import FormConstants from '../../constants/form/form.constants';
import UnitOfMeasureConstants from '../../constants/unitofmeasure.constants';
import IgnoreFields from '../../constants/field-submission-ignores/ignore.constants';
import AutoCompleteServiceData from './AutoCompleteServiceDataMixin';
import RoundingMixin from './RoundingMixin';
import WaterConstants from '../../constants/water.constants';

export default {
  data() {
    return {
      sampleID: 0,
      editMode: false
    };
  },
  mixins: [AutoCompleteServiceData, RoundingMixin],
  computed: {
    maxSampleSeverity() {
      const maxSeverity = Math.max(
        ...Object.values(
          Object.fromEntries(
            Object.entries(this.sampleData).filter(
              ([key]) =>
                key.toLowerCase() !== 'sampleseverityid' && // Oil Sample Severity ID
                key.toLowerCase() !== 'severityid' && // Coolant Sample Severity ID
                key.toLowerCase().endsWith('severityid')
            )
          )
        ),
        1 // This is here to set a default of Normal; otherwise, -Infinity is returned.
      );

      const cptSeverity = this.$store.state.AutoCompletes.Severity.dataSource.find(
        severity => severity.severityId === maxSeverity
      )?.severityName;

      // For some reason Oil and Coolant samples have different sample severity ID property names.

      // Oil Sample Severity ID
      if (Object.hasOwnProperty.call(this.sampleData, 'sampleSeverityID')) {
        this.sampleData.sampleSeverityID = maxSeverity;
      }

      // Coolant Sample Severity ID
      if (Object.hasOwnProperty.call(this.sampleData, 'severityID')) {
        this.sampleData.severityID = maxSeverity;
      }

      return cptSeverity === undefined ? 'Normal' : cptSeverity;
    }
  },
  methods: {
    showAverages() {
      return Object.keys(this.sampleData).some(key => {
        return key.startsWith('average') && this.sampleData[key];
      });
    },
    checkRequired() {
      const errors = [];
      Object.values(this.columnConstants).forEach(fieldInfo => {
        if (fieldInfo.required && !this.sampleData[fieldInfo.field]) {
          errors.push(`${StringHelperService.fieldToTitle(fieldInfo.field)}${FormConstants.Error.RequiredField}`);
        }
      });
      return errors;
    },
    displayResults(sentPromise, stayOnSample) {
      sentPromise
        .then(result => {
          if (!result) {
            return;
          }

          const { data } = result;
          let shouldRefresh = false;

          if (data.oilSampleID) {
            this.sampleData.oilSampleID = data.oilSampleID;
            shouldRefresh = true;
          }
          if (data.coolantSampleID) {
            this.sampleData.coolantSampleID = data.coolantSampleID;
            shouldRefresh = true;
          }
          if (!stayOnSample) {
            this.$nextTick(() => {
              window.open(this.addEditRoute, '_self');
            });
          } else {
            const currentURL = window.location.href;
            const hasQueryParams = currentURL.includes('?');

            if (hasQueryParams && shouldRefresh) {
              window.location.reload();
            } else if (!hasQueryParams && shouldRefresh) {
              const sampleID = data.oilSampleID || data.coolantSampleID;
              window.open(`${currentURL}?id=${sampleID}`, '_self');
            }
          }
        })
        .catch(() => {
          alert('There was a problem processing your form submission.');
        });
    },
    sendData(stayOnSample, isDelete, redirectURL) {
      const submissionData = this.getSubmissionObject();
      if (isDelete) {
        const params = { id: submissionData[this.idField] };
        AjaxDataService.getData(this.deletePrecheckEndpoint, params)
          .then(response => response.data)
          .then(relatedItems => {
            if (relatedItems > 0) {
              alert([`This item cannot be deleted. There are ${JSON.stringify(relatedItems.description)} related items.`]);
              // eslint-disable-next-line no-alert
            } else if (window.confirm('Are you sure you want to delete this item?')) {
              AjaxDataService.deleteData(this.deleteEndpoint, params);
              window.location.href = redirectURL;
            } else {
              // No errors but confirm canceled
            }
          });
      } else if (this.editMode) {
        this.displayResults(AjaxDataService.putData(this.putEndpoint, submissionData), stayOnSample);
      } else {
        this.editMode = true;
        this.displayResults(AjaxDataService.postData(this.putEndpoint, submissionData), stayOnSample);
      }
    },
    onFormSubmit(stayOnSample, isDelete, redirectURL) {
      let errors = [];
      errors = this.checkRequired();
      if (!errors || errors.length === 0) {
        this.sendData(stayOnSample, isDelete, redirectURL);
      } else {
        alert('Please fill out the required fields.');
      }
    },
    getSubmissionObject() {
      const submissionObject = { ...this.sampleData, ...IgnoreFields.oilSampleIgnoreFields };
      Object.keys(submissionObject).forEach(key => submissionObject[key] === undefined && delete submissionObject[key]);
      return this.setFieldDataTypes(submissionObject);
    },
    setFieldDataTypes(data) {
      const clearedObject = { ...data };
      Object.values(this.columnConstants).forEach(fieldInfo => {
        if (data[fieldInfo.field] === '') {
          clearedObject[fieldInfo.field] = fieldInfo?.default;
        } else if (fieldInfo.fieldType === 'datetime' && clearedObject[fieldInfo.field] !== null) {
          clearedObject[fieldInfo.field] = new Date(data[fieldInfo.field]).toJSON();
        }
      });
      return clearedObject;
    },
    formatColumnData(decimalCols, data) {
      const modifiedData = { ...data };
      const dataKeys = Object.keys(modifiedData);

      if (decimalCols !== undefined && decimalCols.length) {
        decimalCols.forEach(({ decimalPlaces, roundDecimals, field, roundOnSave }) => {
          const caseInsensitiveKey = dataKeys.find(key => key.toLowerCase() === field.toLowerCase());
          const dataVal = data[caseInsensitiveKey];

          if (roundOnSave !== undefined && !roundOnSave && dataVal <= WaterConstants.WaterMinimum) {
            return;
          }

          const roundedData = this.roundVal(decimalPlaces, roundDecimals, parseFloat(dataVal));
          modifiedData[caseInsensitiveKey] = roundedData;
        });
      }
      return modifiedData;
    },
    formatRounding(data) {
      let modifiedData = { ...data };
      if (this.gridConstants !== undefined) {
        const decimalCols = this.gridConstants.columns.filter(item => item.roundDecimals !== undefined);
        modifiedData = this.formatColumnData(decimalCols, modifiedData);
      }
      return modifiedData;
    },
    formatDates(data) {
      const modifiedDates = { ...data };
      modifiedDates.dateReceived = this.formatDate(data.dateReceived);
      modifiedDates.dateSampled = this.formatDate(data.dateSampled);
      modifiedDates.createdOn = this.formatDate(data.createdOn);
      modifiedDates.modifiedOn = this.formatDate(data.modifiedOn);
      return modifiedDates;
    },
    formatSampleData(data) {
      const allDataRounded = this.formatRounding(this.formatDates(data));
      return allDataRounded;
    },
    formatDate(value) {
      if (!value) {
        return null;
      }
      return new Date(value).toLocaleDateString();
    },
    async initializeDataParameters() {
      this.sampleID = new URLSearchParams(window.location.search).get('id');
      if (!this.sampleID) {
        this.editMode = true;
      } else {
        this.editMode = false;
      }
    },
    async loadSampleData() {
      if (this.editMode) {
        return this.getDefaultFormData();
      }
      return this.getFormSampleData(this.sampleID);
    },
    async getFormSampleData(sampleID) {
      const { data } = await AjaxDataService.getData(this.getEndpoint, { sampleID });
      // this.getDefaultData gets its properties from this.columnconstants which is oil-sample.column-name.constants.js file

      const defaultData = this.getDefaultData();
      const formattedData = this.formatSampleData(data);
      const returnDataObject = {};
      const defaultDataKeys = Object.keys(defaultData);
      const formatDataKeys = Object.keys(formattedData);

      defaultDataKeys.forEach(dataKey => {
        const matchedKey = formatDataKeys.find(key => key.toLowerCase() === dataKey.toLowerCase());
        if (matchedKey) {
          returnDataObject[matchedKey] = formattedData[matchedKey] ? formattedData[matchedKey] : defaultData[dataKey];
        } else {
          returnDataObject[matchedKey] = defaultData[dataKey];
        }
      });
      const remainingKeys = formatDataKeys.filter(
        key => !defaultDataKeys.some(dataKey => dataKey.toLowerCase() === key.toLowerCase())
      );
      remainingKeys.forEach(key => {
        returnDataObject[key] = formattedData[key];
      });

      return returnDataObject;
    },
    toggleAverageResults() {
      this.showAverageResults = !this.showAverageResults;
    },
    resetFocus(isPreviousSampleAndIsNextDisabled) {
      if (isPreviousSampleAndIsNextDisabled) return;
      this.$refs.basicInfo.resetFocus();
    },
    async getDefaultFormData(batchNumber = {}) {
      this.editMode = false;
      const response = await AjaxDataService.getData(this.newSampleEndpoint, batchNumber);
      const defaultData = this.getDefaultData();
      defaultData.sampleNumber = response?.data.sampleNumber;
      defaultData.previousSampleID = response?.data.previousSampleID;
      defaultData.oilSampleID = response?.data.oilSampleID;
      defaultData.coolantSampleID = response?.data.coolantSampleID;
      return defaultData;
    },
    getDefaultData() {
      const dataItem = {};
      Object.values(this.columnConstants).forEach(fieldInfo => {
        if (fieldInfo.field.toLowerCase().endsWith('severityid')) {
          dataItem[fieldInfo.field] = 1;
        } else {
          dataItem[fieldInfo.field] = fieldInfo.default === undefined ? null : fieldInfo.default;
        }
      });
      dataItem.isActive = true;
      return dataItem;
    },
    setDefaultBracketPropValues(propObject) {
      const returnObj = propObject;
      if (!propObject.displayProp) {
        returnObj.displayProp = `${propObject.valueProp}Display`;
        returnObj.signProp = `${propObject.valueProp}Sign`;
      }
      return returnObj;
    },
    setDefaultAngleBracketPropValues(propObject) {
      const returnObj = propObject;
      if (!propObject.displayProp) {
        returnObj.displayProp = `${propObject.valueProp}Display`;
        returnObj.signProp = `${propObject.valueProp}Sign`;
        returnObj.unitOfMeasureProp = `${propObject.valueProp}UnitOfMeasureCode`;
      }
      return returnObj;
    },
    setAngleBracketDisplayValue(valueProp, displayProp, signProp, unitOfMeasureProp) {
      const props = this.setDefaultAngleBracketPropValues({ valueProp, displayProp, signProp, unitOfMeasureProp });

      if (
        !this.sampleData[props.valueProp] &&
        (this.sampleData[props.unitOfMeasureProp] === UnitOfMeasureConstants.percentUnitOfMeasureCode ||
          this.sampleData[props.unitOfMeasureProp] === null)
      ) {
        this.sampleData[props.displayProp] = '<0.1';
      } else {
        this.sampleData[props.displayProp] = this.sampleData[props.signProp] + this.sampleData[props.valueProp];
      }
    },
    setHardnessAngleBracketDisplayValue(valueProp) {
      const props = this.setDefaultAngleBracketPropValues({ valueProp });

      if (!this.sampleData[props.valueProp]) {
        this.sampleData[props.displayProp] = '<10';
      } else {
        this.sampleData[props.displayProp] = this.sampleData[props.signProp] + this.sampleData[props.valueProp];
      }
    },
    setSymbolData(valueProp, displayProp, signProp) {
      const props = this.setDefaultBracketPropValues({ valueProp, displayProp, signProp });
      if (
        (this.sampleData[props.displayProp] === null || this.sampleData[props.displayProp] === undefined) &&
        (this.sampleData[props.valueProp] === null || this.sampleData[props.valueProp] === undefined)
      ) {
        return;
      }
      this.sampleData[props.signProp] = this.getSignValue(String(this.sampleData[props.displayProp]));
      this.sampleData[props.valueProp] = +String(this.sampleData[props.displayProp]).replace(/[^\d.-]/g, '');
    },
    appendDefaultEmail(defaultEmail, emailList) {
      if (defaultEmail) {
        if (emailList) {
          return `${defaultEmail};${emailList}`;
        }
        return defaultEmail;
      }
      return emailList;
    },
    getSignValue(val) {
      if (val.indexOf('>') > -1) {
        return '>';
      }
      if (val.indexOf('<') > -1) {
        return '<';
      }
      return null;
    },
    async sampleChanged(e) {
      const isExistingSample = this.editMode;
      const sampleDataLookup = await this.getDefaultFormData({ batchNumber: e });
      if (isExistingSample) {
        this.sampleData = sampleDataLookup;
      }
      this.sampleData.sampleNumber = sampleDataLookup.sampleNumber;
    },
    setSampleData(data) {
      if (this.editMode) {
        this.sampleData = { ...this.sampleData, ...data };
      } else {
        this.sampleData = { ...data, ...this.sampleData };
      }
    },
    loadAllData() {
      this.initializeDataParameters();

      if (this.editMode) {
        this.loadMappedAutoCompletes(this.mappings, this.getDefaultData());
        this.loadSampleData(this.endPoint).then(data => {
          this.setSampleData(data);
          this.showAverageResults = this.showAverages();
          this.setAngleBracketDisplayValue('water');
          this.setHardnessAngleBracketDisplayValue('hardness');
        });
      } else {
        this.loadSampleData(this.endPoint).then(data => {
          this.setSampleData(data);
          this.loadMappedAutoCompletes(this.mappings, this.sampleData);
          this.showAverageResults = this.showAverages();
          this.setAngleBracketDisplayValue('water');
          this.setHardnessAngleBracketDisplayValue('hardness');
        });
      }
    }
  }
};
